import { useRoute, useRouter } from 'vue-router'
import { computed, ref, unref, type Component, type ComputedRef } from 'vue'
import unlockAdvantage from '@/assets/icons/unlock-advantage.svg'
import { trackEvent } from '@/services/analytics-service'
import { useCoBrandedCardsAndSubscribedBonuses } from './coBrandPartnersAndSubscribedBonuses'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'

export type SimpleMenuItemType = {
  name: string
  redirectUrl?: string
  label: string
  tracking: string
  icon?: string | Component
  className?: string
}

export type SubItem = Required<SimpleMenuItemType>

export type MenuItemType = SimpleMenuItemType & {
  subItems?: SubItem[]
}

export type MenuItemsType = MenuItemType[]

export const SUB_LEVELS_ROUTES: Record<string, string[]> = {
  'subscription-card-details': ['mySubscriptions'],
  'allsignature-change-plan': ['mySubscriptions']
}

const menuItems: MenuItemsType = [
  {
    name: 'dashboard',
    label: 'navigation.entry.dashboard',
    tracking: 'preview'
  },
  {
    name: 'myBookings',
    label: 'navigation.entry.reservation',
    tracking: 'your reservation'
  },
  {
    name: 'myLoyaltyProgram',
    label: 'navigation.entry.fidelity',
    tracking: 'your loyalty account'
  },
  {
    name: 'globalTransactionHistory',
    label: 'navigation.entry.transactions',
    tracking: 'your account activity'
  },
  {
    name: 'myPartners',
    label: 'partners.header.title',
    tracking: 'your partners' //TODO user story tracking
  },
  {
    name: 'mySubscriptions',
    label: 'navigation.entry.subscriptionCard',
    tracking: 'your subscriptions'
  },
  {
    name: 'my-information',
    label: 'navigation.entry.data.all',
    tracking: 'your information'
  },
  {
    name: 'preferences',
    label: 'preferencePage.title',
    tracking: 'stay preferences'
  },
  {
    name: 'newsletter',
    label: 'navigation.entry.communication',
    tracking: 'your preferences'
  },
  {
    name: 'security-settings',
    label: 'navigation.entry.securitySettings',
    tracking: 'security settings'
  }
]

const unlockAdvantageEntry = {
  name: 'subscriptionUnlockAdvantage',
  redirectUrl: 'https://permalink.accor.com/loyalty-program/subscription-hub?client=ACCOUNT',
  label: 'subscription.navigation.withoutCard',
  tracking: 'unlock more advantages',
  icon: unlockAdvantage,
  className: 'side-nav__unlock-advantage'
}

export const useNavigation = () => {
  const route = useRoute()
  const router = useRouter()
  const userStore = useUserStore()
  const { getCoBrandedCardsFilterByAirline } = useCoBrandedCardsAndSubscribedBonuses()

  const { coBrandedCards, userHasAtLeastOneSubscriptionCard } = storeToRefs(userStore)
  const coBrandAirline = computed(() => getCoBrandedCardsFilterByAirline(coBrandedCards.value))

  const items = computed(() => {
    let data = [...menuItems]

    if (!coBrandAirline.value) {
      data = data.filter((item) => item.name !== 'myPartners')
    }

    if (!userHasAtLeastOneSubscriptionCard.value) {
      data.splice(
        data.findIndex((item) => item.name === 'mySubscriptions'),
        1,
        unlockAdvantageEntry
      )
    }

    return data
  })

  const getRouteConfigByPath = () => SUB_LEVELS_ROUTES[route.name as string] || [route.name] || []
  const selectedItemId = ref(getRouteConfigByPath())

  const findMenuItemBy = (prop: keyof SimpleMenuItemType) => {
    return (value: string, list: SubItem[] | ComputedRef<MenuItemsType> = items) => {
      const unrefList = unref(list)
      return unrefList.find((el) => el[prop] === value)
    }
  }
  const findMenuItemByName = findMenuItemBy('name')

  const setCurrentPath = (
    idList: string[] = [],
    list: SubItem[] | ComputedRef<MenuItemsType> = items
  ) => {
    const [firstId, ...tail] = idList
    const item = findMenuItemByName(firstId, list)

    if (!item) return

    if (item.subItems && tail.length) {
      setCurrentPath(tail, item.subItems)
    } else if (item.name || item.redirectUrl) {
      if (item.tracking) {
        trackEvent('GTM_MENU_ITEM', item.tracking)
      }

      if (item.redirectUrl) {
        window.open(item.redirectUrl, '_blank')
      } else {
        router.push({ name: item.name })
      }
    }
  }

  const updateSelectedItem = (idList: string[] = []) => {
    selectedItemId.value = idList
    setCurrentPath(idList)
  }

  return {
    items,
    selectedItemId,
    updateSelectedItem,
    setCurrentPath,
    getRouteConfigByPath
  }
}
